import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Footer from '../../components/ui/Footer';
import api from '../../utils/api';
import SuccessCelebration from '../../components/ui/SuccessCelebration';
import BlogEditor from './BlogEditor';
import { sanitizeHTML } from '../../utils/sanitize';

const STEP_CONFIG = [
    { title: 'Compose', icon: 'PenLine' },
    { title: 'Preview', icon: 'Eye' },
];

const STATUS_CONFIG = {
    draft: { label: 'Draft', color: 'text-gray-500', bg: 'bg-white border-slate-200', icon: 'FileText' },
    pending_review: { label: 'Pending Review', color: 'text-yellow-600', bg: 'bg-white border-yellow-200', icon: 'Clock' },
    approved: { label: 'Published', color: 'text-emerald-600', bg: 'bg-white border-emerald-200', icon: 'CheckCircle' },
    rejected_admin: { label: 'Rejected', color: 'text-red-500', bg: 'bg-white border-red-200', icon: 'XCircle' },
};

// ─── Tag Input Component ──────────────────────────────────────────────
const TagInput = ({ tags, onChange }) => {
    const [input, setInput] = useState('');

    const addTag = (tagText) => {
        const cleaned = tagText.trim().toLowerCase().replace(/[^a-z0-9\s\-]/g, '').replace(/\s+/g, '-');
        if (cleaned && !tags.includes(cleaned) && tags.length < 5) {
            onChange([...tags, cleaned]);
        }
        setInput('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag(input);
        }
        if (e.key === 'Backspace' && !input && tags.length > 0) {
            onChange(tags.slice(0, -1));
        }
    };

    return (
        <div className="flex flex-wrap items-center gap-2 p-3 border border-slate-200 rounded-xl bg-white focus-within:border-emerald-400 focus-within:ring-4 focus-within:ring-emerald-500/5 transition-all">
            {tags.map((tag, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200">
                    #{tag}
                    <button type="button" onClick={() => onChange(tags.filter((_, idx) => idx !== i))} className="hover:text-red-500 transition-colors">
                        <Icon name="X" size={12} />
                    </button>
                </span>
            ))}
            {tags.length < 5 && (
                <input
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown} 
                    onBlur={() => { if (input.trim()) addTag(input); }}
                    placeholder={tags.length === 0 ? 'Add tags (e.g. react, system-design) — press Enter' : 'Add more...'}
                    className="flex-1 min-w-[140px] border-none outline-none focus:outline-none focus:ring-0 bg-transparent text-sm text-gray-700 placeholder-gray-300"
                />
            )}
            <span className="text-[10px] text-gray-400 font-bold ml-auto">{tags.length}/5</span>
        </div>
    );
};

// ─── My Blogs List ────────────────────────────────────────────────────────────
const MyBlogsList = ({ blogs, onEdit }) => {
    if (!blogs || blogs.length === 0) {
        return (
            <div className="text-center py-16">
                <Icon name="FileText" size={40} className="text-gray-300 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">You haven't submitted any blogs yet.</p>
            </div>
        );
    }

    return (
        <div className="space-y-3 max-w-2xl mx-auto">
            {blogs.map(blog => {
                const cfg = STATUS_CONFIG[blog.moderation_status] || STATUS_CONFIG.pending_review;
                return (
                    <div key={blog.id} className={`flex items-start justify-between gap-4 rounded-xl border p-4 ${cfg.bg}`}>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1.5">
                                <Icon name={cfg.icon} size={14} className={cfg.color} />
                                <span className={`text-[10px] font-boldtracking-wider ${cfg.color}`}>{cfg.label}</span>
                            </div>
                            <p className="text-sm font-semibold text-gray-900 truncate">{blog.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                                {new Date(blog.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                            {blog.moderation_status === 'rejected_admin' && blog.admin_rejection_reason && (
                                <p className="text-xs text-red-500 mt-1.5"><span className="font-semibold">Feedback: </span>{blog.admin_rejection_reason}</p>
                            )}
                            {blog.llm_rejection_reason && (
                                <p className="text-xs mt-1.5 text-indigo-500"><span className="font-semibold">AI Feedback: </span>{blog.llm_rejection_reason}</p>
                            )}
                        </div>
                        <div className="flex flex-col gap-2 flex-shrink-0">
                            {blog.moderation_status === 'approved' && (
                                <>
                                    <a href={`/blogs/${blog.slug}`} target="_blank" rel="noreferrer"
                                        className="px-3 py-1.5 text-xs rounded-lg bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-1">
                                        <Icon name="ExternalLink" size={11} /> View
                                    </a>
                                    <Link to={`/blog-analytics/${blog.id}`}
                                        className="px-3 py-1.5 text-xs rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-600 font-semibold hover:bg-indigo-100 transition-colors flex items-center justify-center gap-1">
                                        <Icon name="BarChart2" size={11} /> Analytics
                                    </Link>
                                </>
                            )}
                            <button onClick={() => onEdit(blog)}
                                className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-gray-700 font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-1">
                                <Icon name="Edit" size={11} /> Edit
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const BlogSubmitPage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [activeTab, setActiveTab] = useState('submit');
    const [form, setForm] = useState({
        title: '', summary: '', coverImageUrl: '', readTimeMinutes: 5, content: '', tags: [],
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [myBlogs, setMyBlogs] = useState(null);
    const [loadingBlogs, setLoadingBlogs] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [draftSaved, setDraftSaved] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const editIdParam = queryParams.get('edit');

    useEffect(() => {
        if (editIdParam && !editingId) {
            fetchBlogForEdit(editIdParam);
        }
    }, [editIdParam]);

    const fetchBlogForEdit = async (id) => {
        try {
            const res = await api.get(`/api/blogs/my/${id}`);
            if (res.data) handleEdit(res.data);
        } catch (err) {
            console.error("Failed to fetch blog for edit:", err);
        }
    };

    const handleEdit = (blog) => {
        setEditingId(blog.id);
        setForm({
            title: blog.title, summary: blog.summary || '', coverImageUrl: blog.cover_image_url || '',
            readTimeMinutes: blog.read_time_minutes || 5, content: blog.content || '',
            tags: blog.tags || [],
        });
        setStep(0);
        setActiveTab('submit');
    };

    const handleChange = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
        setDraftSaved(false);
    };

    // Strip HTML tags for character count validation
    const plainTextLength = (form.content || '').replace(/<[^>]+>/g, '').trim().length;

    const canNext = () => {
        if (step === 0) return form.title.trim().length >= 5 && plainTextLength >= 100;
        return true;
    };

    const fetchMyBlogs = async () => {
        setLoadingBlogs(true);
        try {
            const res = await api.get('/api/blogs/my');
            setMyBlogs(res.data || []);
        } catch (err) {
            console.error("Failed to fetch my blogs:", err);
        }
        setLoadingBlogs(false);
    };

    useEffect(() => {
        if (activeTab === 'my-blogs' && myBlogs === null) fetchMyBlogs();
    }, [activeTab]);

    // Autosave Effect
    useEffect(() => {
        const hasContent = form.title.trim().length >= 5 || plainTextLength >= 10;
        if (activeTab === 'submit' && step === 0 && hasContent && !draftSaved) {
            const timer = setTimeout(() => {
                if (!submitting && !isSavingDraft && !submitSuccess) {
                    handleSubmit(false, true);
                }
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [form, activeTab, step, draftSaved, submitting, isSavingDraft, submitSuccess]);

    const handleSubmit = async (isPublished = true, isAutosave = false) => {
        if (!isAutosave) setSubmitting(true);
        else setIsSavingDraft(true);
        if (!isAutosave) setSubmitError(null);
        try {
            const payload = {
                title: form.title,
                summary: form.summary,
                cover_image_url: form.coverImageUrl ? form.coverImageUrl.substring(0, 500) : '',
                read_time_minutes: form.readTimeMinutes,
                content: form.content,
                is_published: isPublished,
                tags: form.tags,
            };

            if (editingId) {
                await api.put(`/api/blogs/my/${editingId}`, payload);
            } else {
                const res = await api.post('/api/blogs/submit', payload);
                if (res.data?.blog_id && (isAutosave || !isPublished)) {
                    setEditingId(res.data.blog_id);
                    window.history.replaceState(null, '', `?edit=${res.data.blog_id}`);
                }
            }

            if (isPublished) {
                setSubmitSuccess(true);
                setEditingId(null);
            } else {
                setDraftSaved(true);
                setLastSaved(new Date());
                if (!isAutosave) setTimeout(() => setDraftSaved(false), 2000);
            }
        } catch (err) {
            if (!isAutosave) setSubmitError(err.response?.data?.error || err.message || 'Submission failed');
            else console.error("Autosave failed:", err);
        } finally {
            if (!isAutosave) setSubmitting(false);
            else setIsSavingDraft(false);
        }
    };

    // SUCCESS STATE
    if (submitSuccess) {
        return (
            <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <NavigationHeader />
                <SuccessCelebration type="blog" />
                <main className="flex-1 flex flex-col items-center justify-center px-6 pt-16">
                    <div className="text-center max-w-md mt-8">
                        <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-6">
                            <Icon name="CheckCircle" size={32} className="text-emerald-500" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Blog Submitted! 🎉</h1>
                        <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                            Your blog has passed AI review and is <strong className="text-gray-700">waiting for admin approval</strong>. You'll receive an email once it's published.
                        </p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => { setSubmitSuccess(false); setStep(0); setEditingId(null); setForm({ title: '', summary: '', coverImageUrl: '', readTimeMinutes: 5, content: '', tags: [] }); }}
                                className="px-5 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Write Another
                            </button>
                            <button
                                onClick={() => { setActiveTab('my-blogs'); setSubmitSuccess(false); fetchMyBlogs(); }}
                                className="px-5 py-2.5 rounded-lg bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 transition-colors"
                            >
                                View My Blogs
                            </button>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    // MY BLOGS TAB
    if (activeTab === 'my-blogs' && !editingId) {
        return (
            <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <NavigationHeader />
                <main className="flex-1 pt-20 pb-16 px-6">
                    <div className="max-w-2xl mx-auto">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-4">
                                <button onClick={() => setActiveTab('submit')} className="text-xs font-boldtracking-wider text-gray-400 hover:text-gray-900 transition-colors">
                                    ← Back to Editor
                                </button>
                            </div>
                            <button onClick={fetchMyBlogs} className="text-xs text-gray-400 hover:text-gray-700 flex items-center gap-1 transition-colors">
                                <Icon name="RefreshCw" size={12} /> Refresh
                            </button>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-6">My Submissions</h2>
                        {loadingBlogs ? (
                            <div className="flex justify-center py-16">
                                <Icon name="Loader2" size={24} className="text-gray-300 animate-spin" />
                            </div>
                        ) : (
                            <MyBlogsList blogs={myBlogs} onEdit={handleEdit} />
                        )}
                    </div>
                </main>
            </div>
        );
    }

    // COMPOSE / PREVIEW
    return (
        <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <NavigationHeader />

            <main className="flex-1 pt-20 pb-8 px-6 md:px-12 flex flex-col">
                <div className="max-w-[1400px] mx-auto w-full flex flex-col flex-1">
                    {step === 0 ? (
                        /* ── COMPOSE STEP ── */
                        <div className="flex flex-col flex-1 gap-6">
                            {/* Title */}
                            <input
                                type="text"
                                value={form.title}
                                onChange={e => handleChange('title', e.target.value)}
                                placeholder="Enter your blog title..."
                                className="w-full text-3xl md:text-4xl font-bold text-gray-400 placeholder-gray-200 border-none outline-none focus:outline-none focus:ring-0 bg-transparent tracking-tight normal-case"
                                style={{ color: form.title ? '#111827' : undefined, fontStyle: 'normal' }}
                                maxLength={255}
                            />

                            {/* Summary */}
                            <div className="border-l-2 border-gray-200 pl-4">
                                <input
                                    type="text"
                                    value={form.summary}
                                    onChange={e => handleChange('summary', e.target.value)}
                                    placeholder="Write a short summary of this article..."
                                    className="w-full text-sm text-gray-600 placeholder-gray-300 border-none outline-none focus:ring-0 bg-transparent"
                                />
                            </div>

                            {/* Meta */}
                            <div className="flex items-center gap-3 text-xs text-gray-400">
                                <span className="flex items-center gap-1.5">
                                    <Icon name="Clock" size={13} className="text-emerald-500" />
                                    <input
                                        type="number"
                                        min={1} max={60}
                                        value={form.readTimeMinutes}
                                        onChange={e => handleChange('readTimeMinutes', parseInt(e.target.value) || 1)}
                                        className="w-8 text-center border-none outline-none bg-transparent font-bold text-gray-600"
                                    />
                                    MIN READ
                                </span>
                                <span className="text-gray-300">·</span>
                                <span className="font-boldtracking-wider text-gray-400">Technical Article</span>
                            </div>

                            {/* Cover Image */}
                            <div className="relative rounded-xl overflow-hidden bg-gradient-to-br from-emerald-100 via-teal-50 to-cyan-100 h-48 md:h-64 w-full flex items-center justify-center">
                                {form.coverImageUrl ? (
                                    <>
                                        <div className="absolute top-2 left-2 z-20 text-[10px] text-orange-500 bg-white/90 px-2 py-1 rounded font-bold">Max 500 chars</div>
                                        <img src={form.coverImageUrl.substring(0, 500)} alt="Cover" className="w-full h-full object-cover absolute inset-0" onError={e => e.target.style.display = 'none'} />
                                        <button
                                            onClick={() => handleChange('coverImageUrl', '')}
                                            className="relative z-10 bg-white/90 backdrop-blur text-xs font-boldtracking-wider text-gray-700 px-4 py-2 rounded border border-gray-200 hover:bg-white transition-colors"
                                        >
                                            Replace Cover Image
                                        </button>
                                    </>
                                ) : (
                                    <div className="text-center">
                                        <input
                                            type="url"
                                            value={form.coverImageUrl}
                                            onChange={e => handleChange('coverImageUrl', e.target.value)}
                                            placeholder="Paste cover image URL..."
                                            className="bg-white/80 backdrop-blur border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-700 placeholder-gray-400 w-72 focus:outline-none focus:border-emerald-400"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Tags */}
                            <div>
                                <label className="text-xs font-boldtracking-wider text-gray-400 mb-2 block">
                                    Tags
                                </label>
                                <TagInput tags={form.tags} onChange={(tags) => handleChange('tags', tags)} />
                            </div>

                            {/* Rich Text Editor */}
                            <BlogEditor
                                content={form.content}
                                onChange={(html) => handleChange('content', html)}
                                placeholder="Start writing your technical masterpiece..."
                            />
                        </div>
                    ) : (
                        /* ── PREVIEW STEP ── */
                        <div className="space-y-6">
                            <div className="bg-white rounded-[24px] border border-slate-200 p-1.5 shadow-sm">
                                <div className="text-center text-[10px] font-boldtracking-[0.2em] text-slate-400 py-3">
                                    Preview — Live Mode
                                </div>
                                <div className="bg-white rounded-[20px] p-6 md:p-10 relative overflow-hidden">
                                    {form.coverImageUrl && (
                                        <div className="w-full h-[300px] relative -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-6">
                                            <img src={form.coverImageUrl.substring(0, 500)} alt="Cover" className="w-full h-full object-cover" onError={e => e.target.style.display = 'none'} />
                                            <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-transparent" />
                                            <div className="absolute inset-x-0 bottom-0 h-[70%] bg-gradient-to-t from-white via-white/90 to-transparent" />
                                        </div>
                                    )}
                                    <div className={form.coverImageUrl ? '-mt-24 relative z-10' : ''}>
                                        {/* Tags in preview */}
                                        {form.tags.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mb-4">
                                                {form.tags.map((tag, i) => (
                                                    <span key={i} className="text-[10px] font-boldtracking-[0.15em] text-emerald-600">#{tag}</span>
                                                ))}
                                            </div>
                                        )}
                                        <h1 className="text-2xl font-bold text-gray-900 mb-3">{form.title || 'Your Blog Title'}</h1>
                                        <div className="flex items-center gap-3 text-xs text-gray-400 mb-4">
                                            <span className="flex items-center gap-1"><Icon name="Clock" size={13} /> {form.readTimeMinutes} min read</span>
                                        </div>
                                        {form.summary && (
                                            <p className="text-sm text-gray-500 italic border-l-4 border-emerald-400 pl-4 mb-6">{form.summary}</p>
                                        )}
                                        <div className="prose prose-sm prose-gray max-w-none text-sm leading-relaxed">
                                            {form.content ? (
                                                <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(form.content) }} />
                                            ) : (
                                                <p className="text-gray-300">Your content will appear here...</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                                <Icon name="Info" size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                                <div className="text-xs text-gray-600">
                                    <p className="font-semibold text-gray-800 mb-1">Before you submit</p>
                                    <ul className="list-disc pl-4 space-y-0.5">
                                        <li>Content will be reviewed by our AI moderation system</li>
                                        <li>After passing AI check, admin team will review</li>
                                        <li>You'll get an email when published or rejected</li>
                                    </ul>
                                </div>
                            </div>

                            {submitError && (
                                <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600 flex items-start gap-2">
                                    <Icon name="AlertTriangle" size={16} className="flex-shrink-0 mt-0.5" />
                                    <span>{submitError}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main>

            {/* ── BOTTOM BAR ── */}
            <div className="sticky bottom-0 bg-white/95 backdrop-blur-md border-t border-slate-200 z-40 py-2.5 shadow-lg">
                <div className="max-w-[1400px] mx-auto w-full px-6 flex items-center justify-between gap-6">
                    <div className="flex items-center gap-6">
                        <div className="hidden md:flex flex-col">
                            <span className="text-[10px] font-bold text-slate-400tracking-wider">
                                Step {step + 1} of {STEP_CONFIG.length}
                            </span>
                            <span className="text-xs font-bold text-slate-900">{STEP_CONFIG[step].title}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <button onClick={() => handleSubmit(false)} disabled={isSavingDraft} className="text-xs font-boldtracking-widest text-emerald-600 hover:text-emerald-700 transition-colors disabled:opacity-50">
                                Save Draft
                            </button>
                            {isSavingDraft ? (
                                <span className="flex items-center gap-1 text-slate-400 font-boldtracking-widest animate-pulse">
                                    <Icon name="Loader2" size={12} className="animate-spin" /> Saving...
                                </span>
                            ) : draftSaved || lastSaved ? (
                                <span className="flex items-center gap-1 text-emerald-500 font-boldtracking-widest animate-fade-in">
                                    <Icon name="Check" size={12} /> {lastSaved ? `Saved at ${lastSaved.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}` : 'Saved'}
                                </span>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setActiveTab('my-blogs')}
                            className="px-4 py-2 text-xs text-slate-500 hover:text-slate-900 font-boldtracking-wider transition-colors hidden md:block"
                        >
                            My Blogs
                        </button>
                        {editingId && (
                            <button onClick={() => { setEditingId(null); setStep(0); setActiveTab('my-blogs'); }}
                                className="px-6 py-2.5 text-xs text-slate-500 hover:text-slate-900 font-boldtracking-wider transition-colors">
                                Cancel
                            </button>
                        )}
                        {step > 0 && (
                            <button onClick={() => setStep(0)}
                                className="px-6 py-2.5 text-xs text-slate-500 hover:text-slate-900 font-boldtracking-wider transition-colors">
                                ← Back
                            </button>
                        )}
                        {step === 0 ? (
                            <button
                                onClick={() => setStep(1)}
                                disabled={!canNext()}
                                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-blacktracking-widest px-6 py-2.5 rounded-full transition-all shadow-sm shadow-emerald-500/20 disabled:opacity-40 disabled:grayscale"
                            >
                                Next Step <Icon name="ArrowRight" size={14} />
                            </button>
                        ) : (
                            <button
                                onClick={() => handleSubmit(true)}
                                disabled={submitting}
                                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-blacktracking-widest px-6 py-2.5 rounded-full transition-all shadow-sm shadow-emerald-500/20 disabled:opacity-50"
                            >
                                {submitting ? (
                                    <><Icon name="Loader2" size={14} className="animate-spin" /> {editingId ? 'Updating...' : 'Submitting...'}</>
                                ) : (
                                    <><Icon name="Send" size={14} /> {editingId ? 'Update' : 'Publish'}</>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogSubmitPage;
